"use client";

import * as React from "react";
import { Mail, MapPin, Phone, Loader, Send } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Link } from "@/components/common/Link";
import { cn } from "@/lib/utils";
const faqs = [{
  q: "How does the 20% deposit work for your curated journey?",
  a: "To secure your professional guide and motorbikes for your selected dates on the Ha Giang Loop, we require a 20% deposit via PayPal."
}, {
  q: "What is included in this premium adventure?",
  a: "Our small-group motorbike journeys include a professional Easy Rider, fuel, premium boutique accommodation, and authentic local meals throughout the loop."
}, {
  q: "Is the Easy Rider experience suitable for everyone?",
  a: "Absolutely. Our Easy Rider experience is designed for you to travel as a passenger, allowing you to absorb the scenery while a professional driver handles the mountain roads."
}];
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [progressValue, setProgressValue] = React.useState(0);
  React.useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isSubmitting) {
      setProgressValue(0);
      interval = setInterval(() => {
        setProgressValue(prev => prev >= 90 ? 90 : prev + 15);
      }, 300);
    } else if (isSubmitted) {
      setProgressValue(100);
    } else {
      setProgressValue(0);
    }
    return () => clearInterval(interval);
  }, [isSubmitting, isSubmitted]);
  if (isSubmitted && !submitError) {
    return <div data-section-id="222617" className="flex flex-col items-center justify-center space-y-4 rounded-sm border border-border bg-card/50 p-8 text-center backdrop-blur-sm">
        <div className="flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Send className="size-6" />
        </div>
        <h3 className="font-default text-xl font-medium text-foreground">Your Seat Is Reserved</h3>
        <p className="text-muted-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 rounded-sm border-secondary text-secondary hover:bg-secondary hover:text-secondary-foreground">
          Reserve Another Seat
        </Button>
      </div>;
  }
  const inputClasses = "flex w-full rounded-none border-b border-input bg-transparent px-0 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 transition-colors";
  return <div className="relative rounded-sm border border-border bg-card/30 p-6 shadow-sm backdrop-blur-md sm:p-8">
      <div className="mb-6">
        <h3 className="font-default text-2xl font-semibold text-foreground">Journey Overview</h3>
        <p className="mt-2 text-sm text-muted-foreground">Experience a small-group motorbike journey. Select your preferred dates and number of riders to start your curated journey.</p>
      </div>

      <Progress value={progressValue} className={cn("absolute left-0 top-0 h-0.5 w-full rounded-t-sm rounded-b-none bg-transparent transition-opacity duration-300", isSubmitting || progressValue > 0 ? "opacity-100" : "opacity-0")} />

      <Form onSubmit={handleSubmit} className="space-y-5">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-xs uppercase tracking-widest">Name</FormLabel>
              <FormControl>
                <input type="text" className={cn(inputClasses, "h-10")} placeholder="Your name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <div className="grid gap-5 sm:grid-cols-2">
          <FormField control={control} name="email" rules={{
          required: "Email address is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Enter a valid email"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-xs uppercase tracking-widest">Email</FormLabel>
                <FormControl>
                  <input type="email" className={cn(inputClasses, "h-10")} placeholder="email@example.com" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="riders" rules={{
          required: "Please select the number of riders"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-xs uppercase tracking-widest">Number of Riders (max 6)</FormLabel>
                <FormControl>
                  <select className={cn(inputClasses, "h-10 appearance-none")} {...field}>
                    <option value="">Select...</option>
                    {[1, 2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n} {n === 1 ? 'Rider' : 'Riders'}</option>)}
                  </select>
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <FormField control={control} name="message" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-xs uppercase tracking-widest">Travel Dates & Notes</FormLabel>
              <FormControl>
                <textarea className={cn(inputClasses, "min-h-[100px] resize-none")} placeholder="When would you like to travel?" {...field} />
              </FormControl>
              <FormDescription className="text-[10px] italic">A 20% PayPal deposit is required to confirm your booking.</FormDescription>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="rounded-none bg-destructive/10 p-3 text-sm text-destructive border border-destructive/20">
            {submitError}
          </div>}

        <Button asChild className="w-full sm:w-auto rounded-none bg-secondary text-secondary-foreground hover:bg-secondary/90 px-8 py-6">
          <Link to="https://frontiernorthvietnam.com/reserve-your-seat/">
            Reserve Your Seat
          </Link>
        </Button>
      </Form>
    </div>;
}
export default function ContactMessage() {
  return <section id="reserve-your-journey" className="bg-background text-foreground py-20 lg:py-32 relative overflow-hidden">
      {}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-muted/20 blur-[120px]" />
      </div>

      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-start">
          
          {}
          <div className="space-y-10">
            <div>
              <h2 className="font-serif text-5xl font-light tracking-tight sm:text-6xl mb-6">
                Reserve Your Seat
              </h2>
              <p className="text-lg text-muted-foreground max-w-md font-default leading-relaxed">Embark on a small-group motorbike journey through the Ha Giang Loop. Our Easy Rider experience limits groups to six for an intimate, premium adventure.</p>
            </div>

            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-1">
              <div className="flex items-start gap-4">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-none bg-primary/5 text-primary">
                  <MapPin className="size-5" />
                </div>
                <div>
                  <h4 className="font-default font-medium uppercase text-xs tracking-widest">LOCATION</h4>
                  <p className="mt-1 text-sm text-muted-foreground">Ha Giang, Northern Vietnam – the heart of the Ha Giang Loop.</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-none bg-primary/5 text-primary">
                  <Phone className="size-5" />
                </div>
                <div>
                  <h4 className="font-default font-medium uppercase text-xs tracking-widest">WhatsApp</h4>
                  <p className="mt-1 text-sm text-muted-foreground">WhatsApp: +84 326 232 168</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-none bg-primary/5 text-primary">
                  <Mail className="size-5" />
                </div>
                <div>
                  <h4 className="font-default font-medium uppercase text-xs tracking-widest">Email</h4>
                  <p className="mt-1 text-sm text-muted-foreground">Email: frontiernothvietnam@gmail.com</p>
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-border/50">
              <h3 className="font-serif text-2xl font-medium mb-4">Frequently Asked Questions</h3>
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, i) => <AccordionItem key={i} value={`item-${i}`} data-index={i} className="border-b border-border/50">
                    <AccordionTrigger className="text-base font-default hover:text-primary transition-colors py-4">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed font-default pb-4">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>
          </div>

          {}
          <div className="lg:mt-0">
            <FormProvider formKey="reservation_form_home" formId="reservation_form_home" sectionName="ContactMessage" defaultValues={{
            name: "",
            email: "",
            riders: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            riders: {
              required: true
            }
          }} submitText="Reserve Your Seat" successMessage="Your request for a premium curated journey has been received. We will confirm availability and provide deposit details for your small-group motorbike journey.">
              <ContactFormInner />
            </FormProvider>
          </div>

        </div>
      </div>
    </section>;
}